import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProjectModal = ({ project, isOpen, onClose, onNext, onPrevious }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentImageIndex(0);
      setIsImageLoaded(false);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e?.key === 'Escape') {
        onClose();
      } else if (e?.key === 'ArrowLeft' && onPrevious) {
        onPrevious();
      } else if (e?.key === 'ArrowRight' && onNext) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNext, onPrevious]);

  if (!isOpen || !project) return null;

  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  const nextImage = () => {
    if (project?.gallery && currentImageIndex < project?.gallery?.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
      setIsImageLoaded(false);
    }
  };

  const previousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
      setIsImageLoaded(false);
    }
  };

  const currentImage = project?.gallery && project?.gallery?.length > 0 
    ? project?.gallery?.[currentImageIndex] 
    : project?.image;

  const getTechniqueColor = (technique) => {
    const colors = {
      'digital': 'bg-blue-100 text-blue-800',
      'screen': 'bg-green-100 text-green-800',
      'sublimation': 'bg-purple-100 text-purple-800',
      'heat-transfer': 'bg-orange-100 text-orange-800',
      'block': 'bg-amber-100 text-amber-800',
      'embroidery': 'bg-pink-100 text-pink-800'
    };
    return colors?.[technique] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      {/* Modal Content */}
      <div className="relative bg-card rounded-lg shadow-2xl max-w-6xl max-h-[90vh] w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-text-primary">{project?.title}</h2>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTechniqueColor(project?.technique)}`}>
              {project?.technique?.charAt(0)?.toUpperCase() + project?.technique?.slice(1)}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {onPrevious && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onPrevious}
                iconName="ChevronLeft"
                iconSize={16}
              >
                Previous
              </Button>
            )}
            {onNext && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onNext}
                iconName="ChevronRight"
                iconSize={16}
              >
                Next
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              iconName="X"
              iconSize={16}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row max-h-[calc(90vh-80px)]">
          {/* Image Section */}
          <div className="lg:w-2/3 relative bg-muted">
            <div className="relative h-64 lg:h-full min-h-[400px]">
              {!isImageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              )}
              
              <Image
                src={currentImage}
                alt={`${project?.title} - Image ${currentImageIndex + 1}`}
                className={`w-full h-full object-contain transition-opacity duration-300 ${
                  isImageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={handleImageLoad}
              />

              {/* Image Navigation */}
              {project?.gallery && project?.gallery?.length > 1 && (
                <>
                  <button
                    onClick={previousImage}
                    disabled={currentImageIndex === 0}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <Icon name="ChevronLeft" size={20} />
                  </button>
                  
                  <button
                    onClick={nextImage}
                    disabled={currentImageIndex === project?.gallery?.length - 1}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <Icon name="ChevronRight" size={20} />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-75 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {project?.gallery?.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Strip */}
            {project?.gallery && project?.gallery?.length > 1 && (
              <div className="p-4 border-t border-border bg-surface">
                <div className="flex gap-2 overflow-x-auto">
                  {project?.gallery?.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentImageIndex(index);
                        setIsImageLoaded(false);
                      }}
                      className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all duration-200 ${
                        index === currentImageIndex 
                          ? 'border-primary' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <Image
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Details Section */}
          <div className="lg:w-1/3 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Client and Rating */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-secondary">Client</p>
                  <p className="font-medium text-text-primary">{project?.client}</p>
                </div>
                <div className="flex items-center gap-1 text-golden">
                  <Icon name="Star" size={16} fill="currentColor" />
                  <span className="font-medium">{project?.rating}</span>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="font-medium text-text-primary mb-2">Project Description</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{project?.description}</p>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-text-secondary">Material</p>
                  <p className="font-medium text-text-primary">{project?.material}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Industry</p>
                  <p className="font-medium text-text-primary">{project?.industry}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Quantity</p>
                  <p className="font-medium text-text-primary">{project?.quantity} pieces</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Completed</p>
                  <p className="font-medium text-text-primary">{project?.completedDate}</p>
                </div>
              </div>

              {/* Specifications */}
              {project?.specifications && (
                <div>
                  <h3 className="font-medium text-text-primary mb-2">Specifications</h3>
                  <div className="space-y-2">
                    {Object.entries(project?.specifications)?.map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-text-secondary capitalize">{key?.replace('-', ' ')}:</span>
                        <span className="text-text-primary font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Client Testimonial */}
              {project?.testimonial && (
                <div className="bg-muted p-4 rounded-lg">
                  <h3 className="font-medium text-text-primary mb-2">Client Feedback</h3>
                  <p className="text-text-secondary text-sm italic">"{project?.testimonial}"</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="default"
                  fullWidth
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={16}
                >
                  Get Similar Quote
                </Button>
                <Button
                  variant="outline"
                  iconName="Share2"
                  iconSize={16}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;